import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static get targets() {
    return ["primary", "secondary", "modal", "form"];
  }
  
  connect() {
    this.initializeState();
    this.handleAutoLaunch();
    this.setupUI();
    this.setupEventListeners();
    
    // Add observation for removed state changes
    this.observeRemovedStateChanges();
    this.boundHandleItemRemoved = () => this.triggerTotalCalculatorRecalculation();
    window.addEventListener("item.removed", this.boundHandleItemRemoved);
  }
  
  observeRemovedStateChanges() {
    this.removedStateObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          const hasDisabledClass = this.element.classList.contains("disabled");
          const hadDisabledClass = mutation.oldValue && mutation.oldValue.includes("disabled");
          
          // If the disabled state changed, trigger recalculation
          if (hasDisabledClass !== hadDisabledClass) {
            this.handleRemovedStateChange();
          }
        }
      });
    });
    
    this.removedStateObserver.observe(this.element, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ["class"]
    });
  }

  handleRemovedStateChange() {
    // Update the data-total attribute based on removed state
    this.updateTotalAttributesForRemovedState();
    
    // Trigger comprehensive recalculation
    this.triggerTotalCalculatorRecalculation();
    
    // Also trigger item calculator recalculation for this specific item
    this.triggerItemCalculatorRecalculation();
  }
  
  updateTotalAttributesForRemovedState() {
    const totalElement = this.element.querySelector("[data-target*=\"total-calculator.item\"]");
    
    if (totalElement) {
      const isRemoved = this.element.classList.contains("disabled");
      const originalTotal = totalElement.dataset.originalTotal || totalElement.dataset.total;
      
      // Store original total if not already stored
      if (!totalElement.dataset.originalTotal) {
        totalElement.dataset.originalTotal = totalElement.dataset.total;
      }
      
      // Set data-total based on removed state
      totalElement.dataset.total = isRemoved ? "0" : originalTotal;
      
      // Update visual display
      if (isRemoved) {
        totalElement.textContent = "0.00";
        totalElement.classList.add("text-muted");
      } else {
        totalElement.classList.remove("text-muted");
      }
    }
  }

  initializeState() {
    this.openCount = 0;
    this.duplicateTimeout = null; // Use timeout instead of boolean flag
    this.disabledFields = [];
    this.primaryTargets.forEach((primary) => {
      if (primary.disabled) {
        this.disabledFields.push(primary.id);
      }
    });
  }
  
  handleAutoLaunch() {
    if (this.shouldPerformAutoLaunch()) {
      this.scheduleAutoLaunch();
    }
  }
  
  shouldPerformAutoLaunch() {
    const isLineItemProposal = this.isLineItemProposal();
    const shouldLaunch = isLineItemProposal ? this.shouldAutoLaunch() : (!this.persisted() || this.shouldAutoLaunch());
    
    // Don't auto-launch if we just performed a duplication
    if (window.preventAutoLaunchAfterDuplication) {
      return false;
    }
    
    return shouldLaunch && !this.autoLaunchDisabled();
  }
  
  scheduleAutoLaunch() {
    setTimeout(() => {
      const syntheticEvent = new Event("auto-launch");
      syntheticEvent.preventDefault = () => {};
      this.launch(syntheticEvent);
    }, 100);
  }
  
  setupUI() {
    this.initializeTooltips();
    this.setupMutationObserver();
    this.setupChangeTrackers();
  }
  
  setupEventListeners() {
    $(this.modalTarget).on("hide.bs.modal", () => {
      this.handleModalHide();
    });
  }
  
  handleModalHide() {
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      this.secondaryTargets.forEach((secondary) => {
        if (secondary.dataset.fieldName === fieldName) {
          if (!this.disabledFields.includes(primary.id)) {
            primary.disabled = false;
            secondary.disabled = true;
          }
        }
      });
    });
  }
  
  isLineItemProposal() {
    const parentObjectClass = this.data.get("parent-object-class");
    return parentObjectClass === "Bid" || this.element.closest("[data-controller*=\"proposal\"]") !== null;
  }

  autoLaunchDisabled() {
    return this.data.get("disable-auto-launch") === "true";
  }
  
  setupChangeTrackers() {
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      const originalValue = primary.dataset.originalValue;
      
      this.addChangeListener(primary, fieldName, originalValue);
      this.addInputListener(primary, fieldName, originalValue);
      
      // Check initial state
      this.updateChangeTracker(fieldName, primary.value, originalValue);
    });
  }
  
  addChangeListener(primary, fieldName, originalValue) {
    primary.addEventListener("change", (event) => {
      this.updateChangeTracker(fieldName, event.target.value, originalValue);
    });
  }
  
  addInputListener(primary, fieldName, originalValue) {
    if (primary.type === "text" || primary.type === "number") {
      primary.addEventListener("input", (event) => {
        this.updateChangeTracker(fieldName, event.target.value, originalValue);
      });
    }
  }
  
  setupMutationObserver() {
    const lineItemsContainer = document.querySelector("#job_items");
    
    if (lineItemsContainer) {
      this.mutationObserver = new MutationObserver((mutations) => {
        const shouldRecalculate = this.checkForLineItemChanges(mutations);
        
        if (shouldRecalculate) {
          setTimeout(() => {
            this.triggerTotalCalculatorRecalculation();
          }, 100);
        }
      });
      
      this.mutationObserver.observe(lineItemsContainer, {
        childList: true,
        subtree: true
      });
    }
  }
  
  checkForLineItemChanges(mutations) {
    return mutations.some((mutation) => this.isLineItemMutation(mutation));
  }
  
  isLineItemMutation(mutation) {
    if (mutation.type === "childList") {
      const allNodes = [...Array.from(mutation.addedNodes), ...Array.from(mutation.removedNodes)];
      return this.hasLineItemElements(allNodes);
    }
    return false;
  }
  
  hasLineItemElements(nodes) {
    return nodes.some((node) => this.isLineItemNode(node));
  }
  
  isLineItemNode(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }
    
    return this.hasLineItemClass(node) || this.hasLineItemChild(node);
  }
  
  hasLineItemClass(node) {
    return node.classList && node.classList.contains("line-item-proposal-fields");
  }
  
  hasLineItemChild(node) {
    return typeof node.querySelector === "function" && 
           node.querySelector(".line-item-proposal-fields") !== null;
  }
  
  triggerTotalCalculatorRecalculation() {
    // First update all total attributes for removed state
    this.updateAllRemovedStateTotals();
    
    this.triggerItemTotalElements();
    this.dispatchGlobalEvent();
    this.triggerTotalCalculatorElement();
  }
  
  updateAllRemovedStateTotals() {
    const allLineItems = document.querySelectorAll(".line-item-proposal-fields");
    
    allLineItems.forEach(lineItem => {
      const totalElement = lineItem.querySelector("[data-target*=\"total-calculator.item\"]");
      
      if (totalElement) {
        const isRemoved = lineItem.classList.contains("disabled");
        const originalTotal = totalElement.dataset.originalTotal || totalElement.dataset.total;
        
        // Store original total if not already stored
        if (!totalElement.dataset.originalTotal) {
          totalElement.dataset.originalTotal = totalElement.dataset.total;
        }
        // Set data-total based on removed state
        if(isRemoved){
          totalElement.dataset.total = 0;
        }
      }
    });
  }
  
  triggerItemTotalElements() {
    const itemTotalElements = document.querySelectorAll("[data-target*=\"total-calculator.item\"]");
    itemTotalElements.forEach((element) => {
      const event = new CustomEvent("change", { bubbles: true });
      element.dispatchEvent(event);
    });
  }
  
  dispatchGlobalEvent() {
    const globalEvent = new CustomEvent("item.updated", { bubbles: true });
    window.dispatchEvent(globalEvent);
  }
  
  triggerTotalCalculatorElement() {
    const totalCalculatorElement = document.querySelector("[data-controller*=\"total-calculator\"]");
    if (totalCalculatorElement) {
      const changeEvent = new CustomEvent("change", { bubbles: true });
      totalCalculatorElement.dispatchEvent(changeEvent);
    }
  }
  
  triggerItemCalculatorRecalculation() {
    const quantityField = this.element.querySelector("[data-target*=\"item-calculator.quantity\"]");
    const priceField = this.element.querySelector("[data-target*=\"item-calculator.price\"]");
    
    [quantityField, priceField].forEach((field) => {
      if (field) {
        const event = new CustomEvent("input", { bubbles: true });
        field.dispatchEvent(event);
      }
    });
  }
  
  updateChangeTracker(fieldName, currentValue, originalValue) {
    const changeTrackerElement = this.findChangeTrackerElement(fieldName);
    
    if (changeTrackerElement) {
      const shouldShow = this.shouldShowChangeTracker(currentValue, originalValue);
      
      if (shouldShow) {
        changeTrackerElement.classList.remove("hide");
      } else {
        changeTrackerElement.classList.add("hide");
      }
    }
  }

  normalizeValue(value) {
    return (typeof value === "undefined" || value === null) ? "" : String(value).trim();
  }
  
  findChangeTrackerElement(fieldName) {
    const fieldContainer = this.element.querySelector(`[data-field-name="${fieldName}"]`)?.closest(".change-tracker-container");
    return fieldContainer ? fieldContainer.querySelector(".change-tracker") : null;
  }
  
  shouldShowChangeTracker(currentValue, originalValue) {
    const normalizedCurrent = this.normalizeValue(currentValue);
    const normalizedOriginal = this.normalizeValue(originalValue);
    if (this.isPriceField(currentValue, originalValue)) {
      const currentNum = parseFloat(normalizedCurrent);
      const originalNum = parseFloat(normalizedOriginal);
      return Math.abs(currentNum - originalNum) > 0.01;
    }
    
    return normalizedCurrent !== normalizedOriginal && normalizedOriginal !== "";
  }

  isPriceField(currentValue, originalValue) {
    // Check if both values look like decimal numbers
    return /^\d+\.?\d*$/.test(String(currentValue)) && 
          /^\d+\.?\d*$/.test(String(originalValue));
  }
  
  done(e) {
    const isValid = this.validateForm();
    
    if (!isValid) {
      e.preventDefault();
      return false;
    }
    
    this.updatePrimaryFields();
    this.handleCalculations();
    $(this.modalTarget).modal("hide");
  }
  
  updatePrimaryFields() {
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      const originalValue = primary.dataset.originalValue;
      
      this.secondaryTargets.forEach((secondary) => {
        if (secondary.dataset.fieldName === fieldName) {
          primary.value = secondary.value;
          
          this.updateChangeTracker(fieldName, secondary.value, originalValue);
          this.dispatchFieldEvents(primary);
        }
      });
    });
  }
  
  dispatchFieldEvents(primary) {
    const inputEvent = new CustomEvent("input", { bubbles: true });
    primary.dispatchEvent(inputEvent);
    
    const changeEvent = new CustomEvent("change", { bubbles: true });
    primary.dispatchEvent(changeEvent);
  }
  
  handleCalculations() {
    const isNewItem = !this.persisted();
    
    if (isNewItem) {
      this.handleNewItemCalculations();
    } else {
      this.handleExistingItemCalculations();
    }
  }
  
  handleNewItemCalculations() {
    setTimeout(() => {
      this.forceItemCalculatorUpdate();
      
      setTimeout(() => {
        this.triggerTotalCalculatorRecalculation();
        this.dispatchDirectUpdateEvent();
      }, 150);
    }, 100);
  }
  
  handleExistingItemCalculations() {
    setTimeout(() => {
      this.triggerItemCalculatorRecalculation();
      this.triggerTotalCalculatorRecalculation();
      this.dispatchDirectUpdateEvent();
    }, 50);
  }
  
  dispatchDirectUpdateEvent() {
    const event = new CustomEvent("item.directupdated");
    window.dispatchEvent(event);
  }
  
  forceItemCalculatorUpdate() {
    const quantityField = this.element.querySelector("[data-target*=\"item-calculator.quantity\"]");
    const priceField = this.element.querySelector("[data-target*=\"item-calculator.price\"]");
    
    const fields = [quantityField, priceField].filter((field) => field !== null);
    
    fields.forEach((field) => {
      this.triggerFieldEvents(field);
    });
    
    this.triggerItemCalculatorElement();
  }
  
  triggerFieldEvents(field) {
    if (!field || !field.value) {
      return;
    }
    
    this.dispatchInputEvent(field);
    this.dispatchChangeEvent(field);
    this.dispatchRecalculateEvent(field);
  }
  
  dispatchInputEvent(field) {
    const inputEvent = new CustomEvent("input", { bubbles: true, detail: { forced: true } });
    field.dispatchEvent(inputEvent);
  }
  
  dispatchChangeEvent(field) {
    const changeEvent = new CustomEvent("change", { bubbles: true });
    field.dispatchEvent(changeEvent);
  }
  
  dispatchRecalculateEvent(field) {
    if (field.dataset.action && field.dataset.action.includes("item-calculator#recalculate")) {
      const recalcEvent = new CustomEvent("recalculate", { bubbles: true });
      field.dispatchEvent(recalcEvent);
    }
  }
  
  triggerItemCalculatorElement() {
    const itemCalculatorElement = this.element.querySelector("[data-controller*=\"item-calculator\"]");
    if (itemCalculatorElement) {
      const inputEvent = new CustomEvent("input", { bubbles: true });
      itemCalculatorElement.dispatchEvent(inputEvent);
      
      const changeEvent = new CustomEvent("change", { bubbles: true });
      itemCalculatorElement.dispatchEvent(changeEvent);
    }
  }
  
  launch(e) {
    e.preventDefault();
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      this.secondaryTargets.forEach((secondary) => {
        if (secondary.dataset.fieldName === fieldName) {
          secondary.value = primary.value;
          if (!this.disabledFields.includes(primary.id)) {
            secondary.disabled = false;
            primary.disabled = true;
          }
        }
      });
    });
    this.openCount += 1;
    $(this.modalTarget).modal("show");
    return false;
  }
  
  cancel() {
    $(this.modalTarget).modal("hide");
    if (this.openCount === 1) {
      if(this.has_job_line_item() || this.persisted() || this.imported()) {
        return false;
      }
      else{
        this.element.remove();
        $(document.body).removeClass("modal-open");
        this.triggerTotalCalculatorRecalculation();
      }
    }
  }

  persisted() {
    return this.data.get("persisted") !== "false";
  }

  imported() {
    return this.data.get("imported") === "true";
  }

  has_job_line_item() {
    return this.data.get("job-line-item-id") !== null && this.data.get("job-line-item-id") !== "null" && this.data.get("job-line-item-id") !== "";
  }

  shouldAutoLaunch() {
    return this.data.get("auto-launch") === "true";
  }

  initializeTooltips() {
    $(this.element).find("[data-toggle=\"tooltip\"]").tooltip();
  }

  disconnect() {
    window.removeEventListener("item.removed", this.boundHandleItemRemoved);
    $(this.element).find("[data-toggle=\"tooltip\"]").tooltip("destroy");
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    // Clean up the removed state observer
    if (this.removedStateObserver) {
      this.removedStateObserver.disconnect();
    }
    
    // Clean up duplicate timeout
    this.clearDuplicateTimeout();
  }

  // DUPLICATION METHODS - Clean implementation
  duplicate(e) {
    e.preventDefault();
    
    // Simple debounce - prevent rapid clicking for 2 seconds
    if (this.duplicateTimeout) {
      return;
    }
    
    // Set a timeout to prevent rapid clicking
    this.duplicateTimeout = setTimeout(() => {
      this.duplicateTimeout = null;
    }, 2000); // 2 second cooldown
    
    // Check if modal has been opened before (custom fields would be rendered)
    const hasCustomFields = this.modalTarget.querySelector("[name*=\"custom_fields_attributes\"]");
    
    if (hasCustomFields) {
      // Modal has been opened, custom fields are available
      this.performDuplication();
    } else {
      // Need to create custom fields without opening modal
      this.createCustomFieldsInvisibly();
    }
  }

  createCustomFieldsInvisibly() {
    // Sync primary fields to modal first
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      this.secondaryTargets.forEach((secondary) => {
        if (secondary.dataset.fieldName === fieldName) {
          secondary.value = primary.value;
          secondary.disabled = false;
        }
      });
    });
    
    // Create a completely hidden container
    const hiddenContainer = document.createElement('div');
    hiddenContainer.style.position = 'absolute';
    hiddenContainer.style.left = '-9999px';
    hiddenContainer.style.top = '-9999px';
    hiddenContainer.style.width = '1px';
    hiddenContainer.style.height = '1px';
    hiddenContainer.style.overflow = 'hidden';
    hiddenContainer.style.visibility = 'hidden';
    hiddenContainer.style.opacity = '0';
    
    // Clone the modal body to trigger custom field rendering
    const modalBody = this.modalTarget.querySelector('.modal-body');
    if (modalBody) {
      const clonedModalBody = modalBody.cloneNode(true);
      hiddenContainer.appendChild(clonedModalBody);
      
      // Append to document to trigger rendering
      document.body.appendChild(hiddenContainer);
      
      // Small delay to let custom fields render
      setTimeout(() => {
        // Now copy the rendered custom fields back to the original modal
        const customFieldInputs = clonedModalBody.querySelectorAll("[name*=\"custom_fields_attributes\"]");
        customFieldInputs.forEach((clonedInput) => {
          // Find corresponding input in original modal
          const originalInput = this.modalTarget.querySelector(`[name="${clonedInput.name}"]`);
          if (originalInput) {
            originalInput.value = clonedInput.value;
            originalInput.disabled = false;
          } else {
            // Create the input in the original modal if it doesn't exist
            const newInput = clonedInput.cloneNode(true);
            newInput.disabled = false;
            
            // Find a good place to insert it in the modal
            const modalBody = this.modalTarget.querySelector('.modal-body');
            if (modalBody) {
              modalBody.appendChild(newInput);
            }
          }
        });
        
        // Clean up the hidden container
        document.body.removeChild(hiddenContainer);
        
        // Perform duplication with custom fields now available
        this.performDuplication();
      }, 100);
    } else {
      // No modal body found, just perform duplication without custom fields
      this.performDuplication();
    }
  }
  
  performDuplication() {
    const itemAttributes = this.collectItemAttributes();
    const formData = this.buildFormData(itemAttributes);
    
    // Collect custom fields from the modal
    this.addCustomFieldsToFormData(formData);
    this.addMetadataToFormData(formData, itemAttributes);
    
    this.submitDuplicationRequest(formData);
  }
  
  submitDuplicationRequest(formData) {
    // Prevent the new item from auto-launching temporarily
    window.preventAutoLaunchAfterDuplication = true;
    
    fetch("/items/duplicate", {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
      }
    })
    .then((response) => response.text())
    .then((html) => {
      // Handle the duplication response
      this.handleDuplicationResponse(html);
      this.triggerTotalCalculatorRecalculation();
      
      // Small delay then launch the new item's modal
      setTimeout(() => {
        this.launchNewItemModal();
        window.preventAutoLaunchAfterDuplication = false;
      }, 100);
    })
    .catch((error) => {
      console.error("Duplication failed:", error);
      window.preventAutoLaunchAfterDuplication = false;
    });
  }
  
  launchNewItemModal() {
    // Find the newest unpersisted item (the duplicated one)
    const allLineItems = document.querySelectorAll('.line-item-proposal-fields[data-line-items-form-persisted="false"]');
    
    if (allLineItems.length > 0) {
      // Get the last one (most recently added)
      const newestItem = allLineItems[allLineItems.length - 1];
      const launchButton = newestItem.querySelector('[data-action*="line-items-form#launch"]');
      
      if (launchButton) {
        launchButton.click();
      }
    }
  }

  clearDuplicateTimeout() {
    if (this.duplicateTimeout) {
      clearTimeout(this.duplicateTimeout);
      this.duplicateTimeout = null;
    }
  }
  
  // REST OF THE DUPLICATION-RELATED METHODS (collectItemAttributes, addCustomFieldsToFormData, etc.)
  // [Include all the existing duplication helper methods from your current controller...]
  
  collectItemAttributes() {
    const itemAttributes = {};
    this.primaryTargets.forEach((primary) => {
      const fieldName = primary.dataset.fieldName;
      if (fieldName && this.isAllowedFieldName(fieldName)) {
        this.safelyAssignAttribute(itemAttributes, fieldName, primary.value);
      }
    });
    
    const itemTypeField = this.element.querySelector("input[name*=\"[item_type]\"]");
    if (itemTypeField) {
      this.safelyAssignAttribute(itemAttributes, "itemType", itemTypeField.value);
    }
    
    return itemAttributes;
  }
  
  isAllowedFieldName(fieldName) {
    const allowedFields = this.getAllowedKeys();
    return allowedFields.includes(fieldName);
  }
  
  safelyAssignAttribute(obj, key, value) {
    if (typeof key === "string" && !Object.prototype.hasOwnProperty.call(Object.prototype, key)) {
      Object.defineProperty(obj, key, {
        value,
        writable: true,
        enumerable: true,
        configurable: true
      });
    }
  }
  
  buildFormData(itemAttributes) {
    if (!this.isValidItemAttributes(itemAttributes)) {
      return new FormData();
    }
    
    const formData = new FormData();
    const safeKeys = this.getSafeAttributeKeys(itemAttributes);
    this.appendSafeKeysToFormData(formData, safeKeys, itemAttributes);
    return formData;
  }
  
  isValidItemAttributes(itemAttributes) {
    return itemAttributes && 
           typeof itemAttributes === "object" && 
           !Array.isArray(itemAttributes) && 
           itemAttributes.constructor === Object;
  }
  
  getSafeAttributeKeys(itemAttributes) {
    const allowedKeys = this.getAllowedKeys();
    const safeKeys = [];
    
    allowedKeys.forEach((key) => {
      if (this.isKeySafe(itemAttributes, key)) {
        safeKeys.push(key);
      }
    });
    
    return safeKeys;
  }
  
  getAllowedKeys() {
    return [
      "name", "manufacturer_name", "manufacturer_part_number", 
      "uom_id", "quantity", "price", "varied_name", 
      "varied_manufacturer_name", "varied_manufacturer_part_number", 
      "varied_uom_id", "varied_quantity", "varied_price", 
      "itemType", "id", "job_line_item_id", "item_id", "price_ccy"
    ];
  }
  
  isKeySafe(itemAttributes, key) {
    if (!itemAttributes || typeof itemAttributes !== "object") {
      return false;
    }
    return Object.prototype.hasOwnProperty.call(itemAttributes, key);
  }
  
  appendSafeKeysToFormData(formData, safeKeys, itemAttributes) {
    if (!Array.isArray(safeKeys) || !this.isValidFormData(formData)) {
      return;
    }
    
    safeKeys.forEach((key) => this.processSafeKey(formData, key, itemAttributes));
  }
  
  processSafeKey(formData, key, itemAttributes) {
    if (!this.isKeyValidForProcessing(key, itemAttributes)) {
      return;
    }
    
    const value = this.getSafeAttributeValue(itemAttributes, key);
    if (value !== null) {
      this.appendSanitizedAttribute(formData, key, value);
    }
  }
  
  isKeyValidForProcessing(key, itemAttributes) {
    return this.isValidAttributeKey(key) && 
           this.isKeySafe(itemAttributes, key) &&
           this.getAllowedKeys().includes(key);
  }
  
  getSafeAttributeValue(itemAttributes, key) {
    const descriptor = Object.getOwnPropertyDescriptor(itemAttributes, key);
    if (descriptor && descriptor.value !== "undefined") {
      return descriptor.value;
    }
    return null;
  }
  
  appendSanitizedAttribute(formData, key, value) {
    const sanitizedKey = this.sanitizeKey(key);
    const sanitizedValue = this.sanitizeValue(value);
    
    if (this.isSafeToAppend(sanitizedKey, sanitizedValue)) {
      this.appendToFormData(formData, sanitizedKey, sanitizedValue);
    }
  }
  
  appendToFormData(formData, key, value) {
    const safeParamName = this.buildSafeParamName(key);
    if (safeParamName && this.isValidFormData(formData)) {
      formData.append(safeParamName, value);
    }
  }
  
  buildSafeParamName(sanitizedKey) {
    if (!sanitizedKey || typeof sanitizedKey !== "string") {
      return null;
    }
    return `item_attributes[${sanitizedKey}]`;
  }
  
  isValidFormData(formData) {
    return formData && 
           typeof formData === "object" && 
           typeof formData.append === "function";
  }
  
  isSafeToAppend(sanitizedKey, sanitizedValue) {
    return this.isValidKey(sanitizedKey) && this.isValidValue(sanitizedValue);
  }
  
  isValidKey(sanitizedKey) {
    return sanitizedKey && 
           typeof sanitizedKey === "string" &&
           sanitizedKey.length > 0 &&
           sanitizedKey.length <= 50;
  }
  
  isValidValue(sanitizedValue) {
    return (typeof sanitizedValue !== "undefined") && 
           sanitizedValue !== null &&
           typeof sanitizedValue === "string" &&
           sanitizedValue.length <= 1000;
  }
  
  isValidAttributeKey(key) {
    const isValidFormat = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key);
    const isReasonableLength = key.length > 0 && key.length <= 50;
    return isValidFormat && isReasonableLength;
  }
  
  sanitizeValue(value) {
    if (typeof value === "string") {
      return value.trim().slice(0, 1000);
    }
    return String(value);
  }
  
  sanitizeKey(key) {
    if (typeof key !== "string" || key.length === 0) {
      return null;
    }
    return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`).replace(/[^a-z0-9_]/gi, "");
  }
  
  addCustomFieldsToFormData(formData) {
    if (!this.isValidFormDataForCustomFields(formData)) {
      return;
    }
    
    const customFieldContainers = this.element.querySelectorAll("[data-custom-field-id]");
    if (customFieldContainers.length > 0) {
      this.collectCustomFieldsFromContainers(customFieldContainers, formData);
    } else {
      const validInputs = this.getValidCustomFieldInputs();
      this.processValidCustomFieldInputs(validInputs, formData);
    }
  }
  
  collectCustomFieldsFromContainers(containers, formData) {
    containers.forEach((container) => this.processCustomFieldContainer(container, formData));
  }
  
  processCustomFieldContainer(container, formData) {
    const fieldId = container.dataset.customFieldId;
    const input = container.querySelector("input, select, textarea");
    
    if (!input || !fieldId) {
      return;
    }
    
    this.appendCustomFieldValue(input, fieldId, formData);
  }
  
  appendCustomFieldValue(input, fieldId, formData) {
    const value = this.getInputValue(input);
    const fieldType = this.determineCustomFieldType(input);
    
    if (value && fieldType) {
      const paramName = `item_attributes[custom_fields_attributes][${fieldId}][${fieldType}]`;
      formData.append(paramName, value);
    }
  }
  
  determineCustomFieldType(input) {
    if (this.isValueField(input)) {
      return "value";
    }
    
    if (this.isFieldIdField(input)) {
      return "field_id";
    }
    
    if (this.isCustomFieldIdField(input)) {
      return "custom_field_id";
    }
    
    return "value";
  }
  
  isValueField(input) {
    return input.name && input.name.includes("[value]");
  }
  
  isFieldIdField(input) {
    return input.name && input.name.includes("[field_id]");
  }
  
  isCustomFieldIdField(input) {
    return input.name && input.name.includes("[custom_field_id]");
  }
  
  isValidFormDataForCustomFields(formData) {
    return formData && 
           typeof formData.append === "function" && 
           this.modalTarget;
  }
  
  getValidCustomFieldInputs() {
    const selector = "input[name*=\"custom_fields_attributes\"], " +
                    "select[name*=\"custom_fields_attributes\"], " +
                    "textarea[name*=\"custom_fields_attributes\"]";
    
    const customFieldInputs = this.modalTarget.querySelectorAll(selector);
    return Array.from(customFieldInputs);
  }
  
  processValidCustomFieldInputs(validInputs, formData) {
    const fieldGroups = this.createFieldGroups(validInputs);
    this.processFieldGroups(fieldGroups, formData);
  }
  
  createFieldGroups(validInputs) {
    const fieldGroups = new Map();
    
    validInputs.forEach((input) => {
      const match = input.name.match(/custom_fields_attributes\]\[(\d+)\]/);
      if (match) {
        const index = match[1];
        if (!fieldGroups.has(index)) {
          fieldGroups.set(index, []);
        }
        fieldGroups.get(index).push(input);
      }
    });
    
    return fieldGroups;
  }
  
  processFieldGroups(fieldGroups, formData) {
    fieldGroups.forEach((inputGroup) => 
      inputGroup.forEach((input) => this.processCustomField(input, formData))
    );
  }
  
  processCustomField(input, formData) {
    if (!this.isValidCustomFieldForProcessing(input)) {
      return;
    }
    
    const fieldData = this.extractCustomFieldData(input);
    if (!fieldData) {
      return;
    }
    
    this.appendCustomFieldData(input, fieldData, formData);
  }
  
  isValidCustomFieldForProcessing(input) {
    return input && input.name && input.name.includes("custom_fields_attributes");
  }

  extractCustomFieldData(input) {
    const match = input.name.match(/custom_fields_attributes\]\[(\d+)\]\[([^\]]+)\]/);
    if (!match) return null;
    return { index: match[1], fieldType: match[2] };
  }

  appendCustomFieldData(input, fieldData, formData) {
    const value = this.getInputValue(input);
    if (!value && fieldData.fieldType !== "custom_field_id") return;
    const paramName = `item_attributes[custom_fields_attributes][${fieldData.index}][${fieldData.fieldType}]`;
    formData.append(paramName, value);
  }
  
  getInputValue(input) {
    if (!input) {
      return "";
    }
    
    if (this.isCheckboxInput(input)) {
      return this.getCheckboxValue(input);
    }
    
    if (this.isRadioInput(input)) {
      return this.getRadioValue(input);
    }
    
    return this.getStandardInputValue(input);
  }
  
  isCheckboxInput(input) {
    return input.type === "checkbox";
  }
  
  isRadioInput(input) {
    return input.type === "radio";
  }
  
  getCheckboxValue(input) {
    return input.checked ? input.value : "";
  }
  
  getRadioValue(input) {
    return input.checked ? input.value : "";
  }
  
  getStandardInputValue(input) {
    if (input.tagName === "SELECT") {
      return input.value || "";
    }
    return input.value || "";
  }
  
  addMetadataToFormData(formData, itemAttributes) {
    if (!this.isValidFormData(formData)) {
      return;
    }
    
    const parentObjectId = this.data.get("parent-object-id");
    const parentObjectClass = this.data.get("parent-object-class");
    const jobId = this.data.get("job-id");
    
    const itemType = (itemAttributes && itemAttributes.itemType === "0") ? "item" : "service";

    const metadataFields = [
      { key: "object_id", value: parentObjectId },
      { key: "object_clazz", value: parentObjectClass },
      { key: "job_id", value: jobId },
      { key: "type", value: itemType }
    ];

    metadataFields.forEach(({ key, value }) => {
      if (key && value) {
        const sanitizedValue = this.sanitizeValue(String(value));
        formData.append(key, sanitizedValue);
      }
    });
  }
  
  handleDuplicationResponse(html) {
    if (typeof Turbo !== "undefined" && Turbo.renderStreamMessage) {
      Turbo.renderStreamMessage(html);
    } else {
      this.insertHtmlFallback(html);
    }
  }
  
  insertHtmlFallback(html) {
    if (!this.isValidHtml(html)) {
      return;
    }
    
    const container = document.querySelector("#job_items");
    if (container) {
      this.insertSanitizedHtml(container, html);
    }
  }
  
  isValidHtml(html) {
    return html && typeof html === "string" && this.isSafeHtml(html);
  }
  
  insertSanitizedHtml(container, html) {
    const doc = this.parseHtmlSafely(html);
    
    if (!doc || this.containsDangerousElements(doc)) {
      return;
    }
    
    this.appendSafeNodes(container, doc.body);
  }
  
  parseHtmlSafely(html) {
    const parser = new DOMParser();
    return parser.parseFromString(html, "text/html");
  }
  
  appendSafeNodes(container, bodyElement) {
    const safeNodes = this.extractSafeNodes(bodyElement);
    safeNodes.forEach((node) => container.appendChild(node));
  }
  
  containsDangerousElements(doc) {
    const dangerousTags = ["script", "iframe", "object", "embed", "link", "meta", "style"];
    return dangerousTags.some((tag) => doc.querySelector(tag) !== null);
  }
  
  extractSafeNodes(element) {
    const safeNodes = [];
    element.childNodes.forEach((node) => {
      if (this.isSafeNode(node)) {
        const clonedNode = node.cloneNode(true);
        this.sanitizeAttributes(clonedNode);
        safeNodes.push(clonedNode);
      }
    });
    return safeNodes;
  }
  
  isSafeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return true;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      const dangerousTags = ["script", "iframe", "object", "embed", "link", "meta", "style"];
      return !dangerousTags.includes(tagName);
    }
    
    return false;
  }
  
  sanitizeAttributes(element) {
    if (element.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    
    const attributes = Array.from(element.attributes);
    attributes.forEach((attr) => {
      if (this.isDangerousAttribute(attr)) {
        element.removeAttribute(attr.name);
      }
    });
    
    Array.from(element.children).forEach((child) => this.sanitizeAttributes(child));
  }
  
  isDangerousAttribute(attr) {
    const value = (attr.value || "").toLowerCase();
    const dangerousProtocols = ["data:", "script:", "mocha:"];
    
    return attr.name.startsWith("on") || 
           dangerousProtocols.some((protocol) => value.includes(protocol));
  }
  
  isSafeHtml(html) {
    if (!this.isValidHtmlString(html)) {
      return false;
    }
    
    return !this.containsDangerousPatterns(html);
  }
  
  isValidHtmlString(html) {
    return typeof html === "string" && 
           html.length > 0 && 
           html.length <= 10000;
  }
  
  containsDangerousPatterns(html) {
    const dangerousPatterns = [
      /<script/gi, /on\w+\s*=/gi,
      /<iframe/gi, /<object/gi, /<embed/gi, /<link/gi, /<meta/gi, /<style/gi,
      /src\s*=\s*[""]?\s*[a-z]+:/gi, /href\s*=\s*[""]?\s*[a-z]+:/gi
    ];
    
    const scriptPatterns = [
      /data:text\/html/gi
    ];
    
    return dangerousPatterns.some((pattern) => pattern.test(html)) ||
           scriptPatterns.some((pattern) => pattern.test(html));
  }

  validateForm() {
    let isValid = true;
    const requiredFieldSelectors = "textarea:required, input:required, select:required";
    const requiredFields = this.modalTarget.querySelectorAll(requiredFieldSelectors);

    requiredFields.forEach((field) => {
      if (!field.reportValidity()) {
        isValid = false;
        field.classList.add("is-invalid");
        
        const removeInvalidClass = () => {
          field.classList.remove("is-invalid");
          field.removeEventListener("input", removeInvalidClass);
          field.removeEventListener("change", removeInvalidClass);
        };
        
        field.addEventListener("input", removeInvalidClass);
        field.addEventListener("change", removeInvalidClass);
      } else {
        field.classList.remove("is-invalid");
      }
    });

    if (!isValid) {
      console.log("Form validation failed - please fill in all required fields");

      const firstInvalidField = this.modalTarget.querySelector(".is-invalid");
      if (firstInvalidField) {
        firstInvalidField.focus();
      }
    }

    return isValid;
  }
}